import { useMemo, useState } from 'react'
import {Copy, RotateCcw} from 'lucide-react'
import toast from 'react-hot-toast'
import Breadcrumbs from '@/components/Breadcrumbs'
import DisclaimerNote from '@/components/DisclaimerNote'
import FaqAccordion from '@/components/FaqAccordion'
import NumberInput from '@/components/ui/NumberInput'
import SectionHeading from '@/components/ui/SectionHeading'
import StatCard from '@/components/ui/StatCard'
import Seo from '@/components/Seo'
import AdSlot from '@/components/AdSlot'
import { formatCurrency, formatNumber } from '@/lib/format'
import { breadcrumbSchema, faqPageSchema, softwareApplicationSchema } from '@/lib/schema'
import { coinPresets, gradeMultipliers } from '@/data/coinPresets'

const silverPresets = coinPresets.filter(p => p.metal === 'silver')

const faqItems = [
  { question: 'What is melt value vs numismatic value?', answer: 'Melt value is the raw worth of the precious metal content if the coin were melted down, based on spot price, purity, and weight. Numismatic value adds premiums for rarity, condition, mint strike, and collector demand, which can be significantly higher than melt value.' },
  { question: 'How is pure silver weight calculated?', answer: 'Pure metal weight equals the coin\'s total weight in troy ounces multiplied by its purity percentage. For example, a 1 oz .999 fine coin contains 0.999 troy ounces of pure silver.' },
  { question: 'Why do grades like MS-70 command higher premiums?', answer: 'Grading agencies use the Sheldon scale (1-70). Coins graded MS-65 and higher are considered gem-quality with minimal imperfections, making them far more desirable to collectors than circulated coins, which trade closer to melt value.' },
]

function SilverCoinValue() {
  const [presetId, setPresetId] = useState(silverPresets[0].id)
  const [spotPrice, setSpotPrice] = useState(silverPresets[0].defaultSpot)
  const [weightOz, setWeightOz] = useState(silverPresets[0].weightOz)
  const [purity, setPurity] = useState(silverPresets[0].purity * 100)
  const [gradeId, setGradeId] = useState('circulated')

  const preset = silverPresets.find(p => p.id === presetId) ?? silverPresets[0]
  const grade = gradeMultipliers.find(g => g.id === gradeId) ?? gradeMultipliers[0]

  const applyPreset = (id: string) => {
    const p = silverPresets.find(x => x.id === id) ?? silverPresets[0]
    setPresetId(id)
    setSpotPrice(p.defaultSpot)
    setWeightOz(p.weightOz)
    setPurity(p.purity * 100)
  }

  const results = useMemo(() => {
    const pureWeight = weightOz * (purity / 100)
    const meltValue = pureWeight * spotPrice
    const strikePremium = meltValue * (preset.strikePremiumPct / 100)
    const baseMintValue = meltValue + strikePremium
    const gradePremium = baseMintValue * (grade.multiplier - 1)
    const collectorValue = baseMintValue + gradePremium
    return { pureWeight, meltValue, strikePremium, gradePremium, collectorValue }
  }, [weightOz, purity, spotPrice, preset.strikePremiumPct, grade.multiplier])

  const handleReset = () => {
    applyPreset(silverPresets[0].id)
    setGradeId('circulated')
    toast.success('Reset to default values')
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(`Mint Calculator - Silver Coin Value\nCoin: ${preset.name}\nGrade: ${grade.label}\nMelt Value: ${formatCurrency(results.meltValue)}\nEstimated Collector Value: ${formatCurrency(results.collectorValue)}`)
    toast.success('Summary copied to clipboard')
  }

  return (
    <>
      <Seo
        title="Silver Coin Value Calculator - Melt & Numismatic Value"
        description="Calculate the melt value and estimated numismatic collector value of silver coins including American Silver Eagles, Morgan Dollars, junk silver, and Canadian Maple Leafs."
        path="/calculators/silver-coin-value"
        schema={[
          softwareApplicationSchema('Silver Coin Value Calculator', 'Calculate melt value and numismatic collector value of silver bullion and coins.', 'https://www.mintcalculator.com/calculators/silver-coin-value'),
          faqPageSchema(faqItems),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Silver Coin Value Calculator', path: '/calculators/silver-coin-value' }]),
        ]}
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Silver Coin Value Calculator', path: '/calculators/silver-coin-value' }]} />
        <SectionHeading
          eyebrow="Numismatic Coin & Bullion"
          title="Silver Coin Value Calculator"
          description="Estimate the melt floor value and numismatic collector value of silver coins and bullion based on spot price, purity, weight, and condition grade."
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="space-y-5 rounded-xl border border-slate-800 bg-slate-900/50 p-5 lg:col-span-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-300">Coin preset</span>
              <select
                value={presetId}
                onChange={e => applyPreset(e.target.value)}
                className="w-full rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2.5 text-sm text-slate-50 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
              >
                {silverPresets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <NumberInput label="Silver spot price" value={spotPrice} onChange={setSpotPrice} prefix="$" suffix="/oz" step={0.1} accent="amber" />
            <NumberInput label="Coin weight" value={weightOz} onChange={setWeightOz} suffix="oz" step={0.001} accent="cyan" />
            <NumberInput label="Purity" value={purity} onChange={setPurity} suffix="%" step={0.1} accent="teal" />
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-300">Condition grade</span>
              <select
                value={gradeId}
                onChange={e => setGradeId(e.target.value)}
                className="w-full rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2.5 text-sm text-slate-50 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              >
                {gradeMultipliers.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
              </select>
            </label>

            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={handleCopy} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <Copy className="h-4 w-4" />
                Copy Summary
              </button>
              <button onClick={handleReset} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Pure Metal Weight" value={`${formatNumber(results.pureWeight, 4)} oz`} accent="cyan" />
              <StatCard label="Raw Melt Floor Value" value={formatCurrency(results.meltValue)} accent="slate" />
              <StatCard label="Mint Strike Premium" value={formatCurrency(results.strikePremium)} accent="teal" />
              <StatCard label="Grade Premium" value={formatCurrency(results.gradePremium)} accent="amber" />
              <StatCard label="Est. Collector Value" value={formatCurrency(results.collectorValue)} accent="emerald" size="lg" sublabel={grade.label} />
            </div>
            <div className="mt-4">
              <AdSlot variant="inline" />
            </div>
          </div>
        </div>

        <div className="mt-8">
          <DisclaimerNote />
        </div>

        <div className="mt-16 max-w-3xl">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="mt-6">
            <FaqAccordion items={faqItems} />
          </div>
        </div>
      </div>
    </>
  )
}

export default SilverCoinValue
